<template>
    <div
        class="field-panel"
        :class="[isExpanded ? 'field-panel--expanded' : '', hasConditions ? 'field-panel--has-conditions' : '']"
    >
        <!-- Header - clickable to expand/collapse -->
        <header
            class="field-panel__header"
            :class="{ 'field-panel__header--expanded': isExpanded }"
            @click="toggleExpanded"
        >
            <div class="field-panel__drag-handle cursor-grab">
                <icon icon="hero/solid/ellipsis-vertical" class="w-4 h-4" />
            </div>

            <div class="field-panel__index">
                {{ index + 1 }}
            </div>

            <div class="field-panel__info">
                <span class="field-panel__name">
                    {{ field.name || lang('fields::fields.field-editor.unnamed_field') }}
                </span>
                <span class="field-panel__slug">
                    {{ field.slug || '...' }}
                </span>
            </div>

            <div class="field-panel__badges">
                <span v-if="field.type" class="field-panel__badge field-panel__badge--type">
                    {{ getTypeName(field.type) }}
                </span>
                <span v-if="field.required === true || field.required === 1 || field.required === '1'" class="field-panel__badge field-panel__badge--required">
                    {{ lang('fields::fields.field-editor.required_short') }}
                </span>
                <icon
                    v-if="hasConditions"
                    v-tooltip="lang('fields::fields.field-editor.has_conditions')"
                    icon="hero/outline/funnel"
                    class="field-panel__condition-icon"
                />
            </div>

            <div class="field-panel__chevron">
                <icon
                    icon="hero/outline/chevron-down"
                    class="field-panel__chevron-icon"
                    :class="{ 'field-panel__chevron-icon--rotated': isExpanded }"
                />
            </div>
        </header>

        <!-- Content - shown when expanded -->
        <main v-show="isExpanded" class="field-panel__content">
            <!-- Tabs -->
            <div class="field-panel__tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    type="button"
                    class="field-panel__tab"
                    :class="[activeTab === tab.id ? 'field-panel__tab--active' : '']"
                    @click.stop="activeTab = tab.id"
                >
                    {{ tab.name }}
                </button>
            </div>

            <!-- Tab Content -->
            <div class="field-panel__tab-content">
                <!-- General Tab -->
                <div v-show="activeTab === 'general'" class="field-settings">
                    <FieldGeneralSettings :field="field" :types="types" :sibling-field-slugs="siblingFieldSlugs" @update:field="emit('update:field', $event)" />
                </div>

                <!-- Appearance Tab -->
                <div v-show="activeTab === 'appearance'" class="field-settings">
                    <FieldAppearanceSettings :field="field" @update:field="emit('update:field', $event)" />
                </div>

                <!-- Validation Tab -->
                <div v-show="activeTab === 'validation'" class="validation-editor">
                    <ValidationRulesEditor
                        :validation="field.validation || {}"
                        :field-type="field.type"
                        @update:validation="updateValidation"
                    />
                </div>

                <!-- Conditional Tab -->
                <div v-show="activeTab === 'conditional'" class="condition-builder">
                    <ConditionalLogicBuilder
                        :conditions="field.conditions || {}"
                        :available-fields="availableFields"
                        @update:conditions="updateConditions"
                    />
                </div>

                <!-- Advanced Tab -->
                <div v-show="activeTab === 'advanced'" class="field-advanced">
                    <Field
                        :model-value="field.display"
                        :field="{
                            name: lang('fields::fields.field-editor.in_datagrid'),
                            slug: 'display',
                            type: 'checkbox',
                            adminComponent: 'AdminCheckbox',
                        }"
                        label-position="left"
                        @update:model-value="updateFieldProperty('display', $event)"
                    />

                    <Field
                        :model-value="field.displayRepeaterHeader"
                        :field="{
                            name: lang('fields::fields.field-editor.display_repeater_header'),
                            slug: 'displayRepeaterHeader',
                            type: 'checkbox',
                            adminComponent: 'AdminCheckbox',
                        }"
                        label-position="left"
                        @update:model-value="updateFieldProperty('displayRepeaterHeader', $event)"
                    />

                    <Field
                        :model-value="field.has_tenancy"
                        :field="{
                            name: lang('fields::fields.field-editor.is_tenancy'),
                            slug: 'has_tenancy',
                            type: 'checkbox',
                            adminComponent: 'AdminCheckbox',
                        }"
                        label-position="left"
                        @update:model-value="updateFieldProperty('has_tenancy', $event)"
                    />

                    <Field
                        :model-value="field.has_foreign_id"
                        :field="{
                            name: lang('fields::fields.field-editor.has_foreign_id'),
                            slug: 'has_foreign_id',
                            type: 'checkbox',
                            adminComponent: 'AdminCheckbox',
                        }"
                        label-position="left"
                        @update:model-value="updateFieldProperty('has_foreign_id', $event)"
                    />

                    <Field
                        v-if="field.has_foreign_id"
                        :model-value="field.foreign_id"
                        :field="{
                            name: lang('fields::fields.field-editor.foreign_id'),
                            slug: 'foreign_id',
                            type: 'text',
                            adminComponent: 'AdminText',
                        }"
                        label-position="left"
                        @update:model-value="updateFieldProperty('foreign_id', $event)"
                    />

                </div>
            </div>

            <!-- Footer Actions -->
            <div class="field-panel__footer">
                <Button type="button" size="sm" variant="danger" @click.stop="emit('remove')">
                    <icon icon="hero/outline/trash" class="w-4 h-4 mr-1" />
                    {{ lang('fields::fields.field-editor.remove_field') }}
                </Button>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { lang } from '@/lang/composable';
import FieldGeneralSettings from './FieldGeneralSettings.vue';
import FieldAppearanceSettings from './FieldAppearanceSettings.vue';
import ValidationRulesEditor from './ValidationRulesEditor.vue';
import ConditionalLogicBuilder from './ConditionalLogicBuilder.vue';

interface FieldData {
    slug: string;
    name: string;
    type: string;
    required?: boolean;
    multiple?: boolean;
    display?: boolean;
    group?: string;
    labelPosition?: string;
    tooltip?: string;
    has_tooltip?: boolean;
    foreign_id?: string;
    has_foreign_id?: boolean;
    mask?: string;
    options?: any[];
    displayRepeaterHeader?: boolean;
    has_tenancy?: boolean;
    validation?: Record<string, any>;
    conditions?: Record<string, any>;
    has_condition?: boolean;
    width?: string | null;
    placeholder?: string;
    help?: string;
    wrapperClass?: string;
    [key: string]: any;
}

interface Props {
    field: FieldData;
    index: number;
    types: { id: string; name: string; admin?: Record<string, any> }[];
    availableFields: { slug: string; name: string }[];
    siblingFieldSlugs?: string[];
    expanded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    expanded: false,
    siblingFieldSlugs: () => [],
});

const emit = defineEmits<{
    (event: 'update:field', field: FieldData): void;
    (event: 'remove'): void;
    (event: 'update:expanded', value: boolean): void;
}>();

const isExpanded = ref(props.expanded);
const activeTab = ref('general');

const tabs = computed(() => [
    { id: 'general', name: lang('fields::fields.field-editor.tab_general') },
    { id: 'appearance', name: lang('fields::fields.field-editor.tab_appearance') },
    { id: 'validation', name: lang('fields::fields.field-editor.tab_validation') },
    { id: 'conditional', name: lang('fields::fields.field-editor.tab_conditional') },
    { id: 'advanced', name: lang('fields::fields.field-editor.tab_advanced') },
]);

// Helper to check if a condition rule is meaningful (has actual values)
const isValidConditionRule = (rule: any): boolean => {
    if (!rule) return false;
    // A rule is valid if it has a non-null/non-empty field
    return rule.field !== null && rule.field !== undefined && rule.field !== '';
};

const hasConditions = computed(() => {
    if (!props.field.conditions) return false;

    // Check legacy array format
    if (Array.isArray(props.field.conditions)) {
        // Filter to only count meaningful conditions
        return props.field.conditions.some(isValidConditionRule);
    }

    // Check new format with rules array
    if (props.field.conditions.rules && Array.isArray(props.field.conditions.rules)) {
        return props.field.conditions.rules.some(isValidConditionRule);
    }

    return false;
});

const getTypeName = (typeId: string) => {
    const type = props.types.find((t) => t.id === typeId);
    return type?.name ?? typeId;
};

const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
    emit('update:expanded', isExpanded.value);
};

const updateValidation = (validation: Record<string, any>) => {
    emit('update:field', { ...props.field, validation });
};

const updateConditions = (conditions: Record<string, any>) => {
    const hasConditions = conditions.rules && conditions.rules.length > 0;
    emit('update:field', {
        ...props.field,
        conditions,
        has_condition: hasConditions,
    });
};

const updateFieldProperty = (key: string, value: any) => {
    emit('update:field', { ...props.field, [key]: value });
};

const expand = () => {
    isExpanded.value = true;
};

defineExpose({ expand });
</script>
